import type { SuretyBondPreview } from "@portal/api/bond/agent";
import { FormItemLabel } from "@portal/components/form";
import {
	FormCurrencyComponent,
	FormCurrencyPercentComponent,
	FormDropdownComponent,
} from "@portal/components/form/fieldLayouts";
import { FormInput } from "@portal/components/form/fields";
import { useFeatureFlag } from "@portal/hooks/useFeatureFlag";
import { useContext, useEffect, useRef } from "react";
import type { Path } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import { QuickSubmitFormItemGroup } from "../../../../account/create/quick-submit/QuickSubmitFormItemGroup";
import { BidBondSelector } from "../../../../shared/submission/BidBondSelector";
import { AdminBondRequestContext } from "../../AdminBondRequestContext";
import type { AdminBondRequestFormData } from "../../schema";

const bondTypeOptions = [
	{ label: "Bid Bond", value: "bid" },
	{ label: "Final Bond", value: "final" },
	{ label: "Bid to Final Bond", value: "bid_to_final" },
];

const quoteFieldsToClear: Path<AdminBondRequestFormData>[] = [
	"quote.bondAmount",
	"quote.bidAmount",
	"quote.bondAmountPercentOfBid",
	"quote.bidBondAmountType",
	"quote.contractAmount",
	"quote.project.description",
];

export const BondSection = () => {
	const formMethods = useFormContext<AdminBondRequestFormData>();
	const { control, watch, resetField } = formMethods;
	const isExtractionEnabled = useFeatureFlag("bond_request_extraction");
	const bondType = watch("quote.contractSuretyType");
	const prevBondTypeRef = useRef(bondType);

	useEffect(() => {
		if (prevBondTypeRef.current !== undefined && bondType !== prevBondTypeRef.current) {
			for (const field of quoteFieldsToClear) {
				resetField(field);
			}
		}
		prevBondTypeRef.current = bondType;
	}, [bondType, resetField]);

	return (
		<>
			<QuickSubmitFormItemGroup className="col-span-2" data-testid="bond-type">
				<FormDropdownComponent
					formMethods={formMethods}
					name={"quote.contractSuretyType"}
					label="Bond Type"
					placeholder="Select One"
					options={bondTypeOptions}
					anchor="bottom start"
					compact
				/>
			</QuickSubmitFormItemGroup>
			<BondTypeSpecificFields />
			{bondType !== undefined && !isExtractionEnabled && (
				<div className="col-span-3 flex flex-col gap-[13px]">
					<FormItemLabel name="quote.project.description">Project Description</FormItemLabel>
					<FormInput name={"quote.project.description"} control={control} compact />
				</div>
			)}
		</>
	);
};

const useSyncBondToContract = () => {
	const formMethods = useFormContext<AdminBondRequestFormData>();
	const contractAmountTouched = useRef(false);
	const bondType = formMethods.watch("quote.contractSuretyType");

	useEffect(() => {
		contractAmountTouched.current = false;
	}, [bondType]);

	const onBondAmountBlur = () => {
		if (contractAmountTouched.current) return;
		const bondAmount = formMethods.getValues("quote.bondAmount");
		if (bondAmount) {
			formMethods.setValue("quote.contractAmount", bondAmount);
		}
	};

	const onContractAmountChange = () => {
		contractAmountTouched.current = true;
	};

	return { onBondAmountBlur, onContractAmountChange };
};

const BondTypeSpecificFields = () => {
	const formMethods = useFormContext<AdminBondRequestFormData>();
	const { watch } = formMethods;
	const bondType = watch("quote.contractSuretyType");
	const { onBondAmountBlur, onContractAmountChange } = useSyncBondToContract();

	if (!bondType) return null;

	if (bondType === "bid") {
		return (
			<>
				<QuickSubmitFormItemGroup data-testid="bid-amount">
					<FormCurrencyComponent
						formMethods={formMethods}
						name={"quote.bidAmount"}
						label="Bid Amount"
						compact
					/>
				</QuickSubmitFormItemGroup>
				<QuickSubmitFormItemGroup data-testid="bid-bond-amount">
					<FormCurrencyPercentComponent
						currencyName={"quote.bondAmount"}
						percentName={"quote.bondAmountPercentOfBid"}
						amountTypeName={"quote.bidBondAmountType"}
						formMethods={formMethods}
						referenceSourceName={"quote.bidAmount"}
						defaultAmountType="percent"
						compact
					/>
				</QuickSubmitFormItemGroup>
			</>
		);
	}

	if (bondType === "final") {
		return (
			<>
				<QuickSubmitFormItemGroup data-testid="bond-amount">
					<FormCurrencyComponent
						formMethods={formMethods}
						name={"quote.bondAmount"}
						label="Bond Amount"
						compact
						onValueBlur={onBondAmountBlur}
					/>
				</QuickSubmitFormItemGroup>
				<QuickSubmitFormItemGroup data-testid="contract-amount">
					<FormCurrencyComponent
						formMethods={formMethods}
						name={"quote.contractAmount"}
						label="Contract Amount"
						compact
						onValueChange={onContractAmountChange}
					/>
				</QuickSubmitFormItemGroup>
			</>
		);
	}

	// bid_to_final
	return (
		<>
			<QuickSubmitFormItemGroup data-testid="bond-amount">
				<FormCurrencyComponent
					formMethods={formMethods}
					name={"quote.bondAmount"}
					label="Bond Amount"
					compact
					onValueBlur={onBondAmountBlur}
				/>
			</QuickSubmitFormItemGroup>
			<QuickSubmitFormItemGroup data-testid="contract-amount">
				<FormCurrencyComponent
					formMethods={formMethods}
					name={"quote.contractAmount"}
					label="Contract Amount"
					compact
					onValueChange={onContractAmountChange}
				/>
			</QuickSubmitFormItemGroup>
			<div className="col-span-3">
				<BidToFinalBidBondSelector />
			</div>
		</>
	);
};

const BidToFinalBidBondSelector = () => {
	const { setValue, resetField, watch } = useFormContext<AdminBondRequestFormData>();
	const { selectedAccount } = useContext(AdminBondRequestContext);
	const selectedBondId = watch("quote.bidBondId");

	const handleSelectBidBond = (bondPreview?: SuretyBondPreview) => {
		if (!bondPreview) {
			resetField("quote.bidBondId");
			resetField("quote.actualBidAmount");
			resetField("quote.actualBidDate");
			resetField("quote.project.description");
			return;
		}
		setValue("quote.bidBondId", bondPreview.id);
		setValue("quote.actualBidAmount", bondPreview.amount);
		setValue("quote.actualBidDate", bondPreview.effectiveDate);
		if (bondPreview.description) {
			setValue("quote.project.description", bondPreview.description);
		}
		if (bondPreview.underwriterUserId) {
			setValue("underwriterId", bondPreview.underwriterUserId);
		}
		if (bondPreview.agentId) {
			setValue("agentId", bondPreview.agentId);
		}
	};

	return (
		<QuickSubmitFormItemGroup data-testid="bid-bond-selector">
			<FormItemLabel name="quote.bidBondId">Select Bid Bond</FormItemLabel>
			<BidBondSelector
				accountId={selectedAccount?.id}
				onSelect={handleSelectBidBond}
				selectedBondId={selectedBondId}
				buttonLabel="Select Bid Bond"
			/>
		</QuickSubmitFormItemGroup>
	);
};
